import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { FormControl } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { RrelinkingService } from '../../../services/rrelinking.service';
import { OfficeOption } from '../../../models/offices.model';
import { UnlinkedRre, RreLinkModalResult } from '../../../models/rrelinking.model';
import { openAutocompletePanel, toggleAutocompletePanel } from '../../../shared/utils/autocomplete-panel.utils';

@Component({
  selector: 'app-rre-link-modal',
  standalone: false,
  templateUrl: './rre-link-modal.component.html',
  styleUrl: './rre-link-modal.component.css',
})
export class RreLinkModalComponent implements OnInit {
  @Output() submitRequest = new EventEmitter<RreLinkModalResult>();
  officeOptions: OfficeOption[] = [];
  rreOptions: UnlinkedRre[] = [];

  officeControl = new FormControl<string | OfficeOption>('');
  rreControl = new FormControl<string | UnlinkedRre>({ value: '', disabled: true });

  filteredOfficeOptions$!: Observable<OfficeOption[]>;
  filteredRreOptions$: Observable<UnlinkedRre[]> = of([]);

  private selectedOffice: OfficeOption | null = null;
  private selectedRreOption: UnlinkedRre | null = null;

  loadingOffices = false;
  loadingRres = false;
  officesErrorMessage = '';
  rresErrorMessage = '';
  validationErrors: string[] = [];
  dialogError = '';

  constructor(
    public dialogRef: MatDialogRef<RreLinkModalComponent, RreLinkModalResult>,
    private rrelinkingService: RrelinkingService
  ) {}

  ngOnInit(): void {
    this.filteredOfficeOptions$ = this.officeControl.valueChanges.pipe(
      startWith(''),
      map(value => {
        const name = typeof value === 'string' ? value : (value?.mailingName ?? '');
        return name ? this._filterOffices(name) : this.officeOptions.slice();
      })
    );

    this.loadingOffices = true;
    this.officesErrorMessage = '';
    this.rrelinkingService.getOffices().subscribe({
      next: (offices) => {
        this.officeOptions = offices;
        this.officeControl.setValue('');
        this.loadingOffices = false;
      },
      error: () => {
        this.officesErrorMessage = 'Failed to load offices.';
        this.loadingOffices = false;
      },
    });
  }

  displayOfficeFn(option: OfficeOption | string | null): string {
    if (!option) return '';
    if (typeof option === 'string') return option;
    return option.mailingName
      ? `${option.mailingName} - ${option.officeKey}`
      : String(option.officeKey);
  }

  displayRreFn(option: UnlinkedRre | string | null): string {
    if (!option) return '';
    if (typeof option === 'string') return option;
    return option.rre + (option.rreName ? ' - ' + option.rreName : '');
  }

  onOfficeSelected(option: OfficeOption): void {
    this.selectedOffice = option;
    this.selectedRreOption = null;
    this.rreControl.setValue('');
    this.rreControl.disable();
    this.rreOptions = [];
    this.rresErrorMessage = '';

    this.loadingRres = true;
    this.rrelinkingService.getUnlinkedRREs(option.claimOfficeId).subscribe({
      next: (rres) => {
        this.rreOptions = rres;
        this.loadingRres = false;
        this.rreControl.enable();
        this.filteredRreOptions$ = this.rreControl.valueChanges.pipe(
          startWith(''),
          map(value => {
            const name = typeof value === 'string' ? value : (value ? this.displayRreFn(value) : '');
            return name ? this._filterRres(name) : this.rreOptions.slice();
          })
        );
        this.rreControl.setValue('');
      },
      error: () => {
        this.rresErrorMessage = 'Failed to load unlinked RREs.';
        this.loadingRres = false;
      },
    });
  }

  onRreSelected(option: UnlinkedRre): void {
    this.selectedRreOption = option;
  }

  clearOffice(evt: Event): void {
    evt.stopPropagation();
    this.officeControl.reset();
    this.selectedOffice = null;
    this.selectedRreOption = null;
    this.rreControl.setValue('');
    this.rreControl.disable();
    this.rreOptions = [];
    this.filteredRreOptions$ = of([]);
  }

  clearRre(evt: Event): void {
    evt.stopPropagation();
    this.rreControl.reset();
    this.selectedRreOption = null;
  }

  openOrClosePanel(evt: Event, trigger: MatAutocompleteTrigger): void {
    toggleAutocompletePanel(evt, trigger);
  }

  openPanel(trigger: MatAutocompleteTrigger): void {
    openAutocompletePanel(trigger);
  }

  onSubmit(): void {
    this.validationErrors = this.validate();
    if (this.validationErrors.length > 0) return;
    this.dialogError = '';
    this.submitRequest.emit({
      claimOfficeId: String(this.selectedOffice!.claimOfficeId),
      officeName: this.selectedOffice!.mailingName,
      rre: this.selectedRreOption!.rre,
      rreName: this.selectedRreOption!.rreName ?? '',
    });
  }

  onCancel(): void {
    this.dialogRef.close(undefined);
  }

  private validate(): string[] {
    const errors: string[] = [];
    if (!this.selectedOffice) errors.push('Office is required.');
    if (!this.selectedRreOption) errors.push('RRE is required.');
    return errors;
  }

  private _filterOffices(name: string): OfficeOption[] {
    const filterValue = name.toLowerCase();
    return this.officeOptions.filter(o =>
      o.mailingName.toLowerCase().includes(filterValue) ||
      String(o.officeKey).includes(filterValue)
    );
  }

  private _filterRres(name: string): UnlinkedRre[] {
    const filterValue = name.toLowerCase();
    return this.rreOptions.filter(r =>
      r.rre.toLowerCase().includes(filterValue) || r.rreName.toLowerCase().includes(filterValue)
    );
  }
}
